/**
 * Channels API Service
 *
 * Wraps /api/channels endpoints. Channels are either live streams
 * (HLS/Icecast/etc.) or mix_rotation (an ordered rotation of DJ mix content).
 */

import { apiClient } from '../config';
import type { Channel, ChannelMix, ChannelStatus, ChannelTestResult } from '../types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface ChannelListParams {
  page?: number;
  size?: number;
  organization_id?: string;
  type?: 'live_stream' | 'mix_rotation';
  is_active?: boolean;
  scope?: 'platform' | 'organization' | 'site';
}

function buildQueryString(params: Record<string, unknown> | undefined): string {
  if (!params) return '';
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && String(v) !== '') {
      clean[k] = String(v);
    }
  }
  const qs = new URLSearchParams(clean).toString();
  return qs ? `?${qs}` : '';
}

export class ChannelsService {
  static async list(params?: ChannelListParams): Promise<PaginatedResponse<Channel>> {
    const response = await apiClient.get<PaginatedResponse<Channel>>(
      `/api/channels${buildQueryString(params as Record<string, unknown>)}`
    );
    return response.data;
  }

  static async get(id: string): Promise<Channel> {
    const response = await apiClient.get<ApiResponse<Channel>>(`/api/channels/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch channel');
    return response.data.data;
  }

  static async create(data: Partial<Channel>): Promise<Channel> {
    const response = await apiClient.post<ApiResponse<Channel>>('/api/channels', data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create channel');
    return response.data.data;
  }

  static async update(id: string, data: Partial<Channel>): Promise<Channel> {
    const response = await apiClient.put<ApiResponse<Channel>>(`/api/channels/${id}`, data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update channel');
    return response.data.data;
  }

  static async remove(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/channels/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete channel');
  }

  static async status(id: string): Promise<ChannelStatus> {
    const response = await apiClient.get<ApiResponse<ChannelStatus>>(`/api/channels/${id}/status`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to check status');
    return response.data.data;
  }

  static async test(id: string): Promise<ChannelTestResult> {
    const response = await apiClient.post<ApiResponse<ChannelTestResult>>(`/api/channels/${id}/test`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to test channel');
    return response.data.data;
  }

  // Mix rotation membership (used by PR C — picker/reorder UI)
  static async listMixes(id: string): Promise<ChannelMix[]> {
    const response = await apiClient.get<ApiResponse<ChannelMix[]>>(`/api/channels/${id}/mixes`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to list mixes');
    return response.data.data;
  }

  static async addMix(
    id: string,
    content_id: string,
    position?: number
  ): Promise<ChannelMix> {
    const response = await apiClient.post<ApiResponse<ChannelMix>>(`/api/channels/${id}/mixes`, {
      content_id,
      position,
    });
    if (!response.data.success) throw new Error(response.data.error || 'Failed to add mix');
    return response.data.data;
  }

  static async moveMix(id: string, mixId: string, position: number): Promise<ChannelMix> {
    const response = await apiClient.put<ApiResponse<ChannelMix>>(
      `/api/channels/${id}/mixes/${mixId}`,
      { position }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to move mix');
    return response.data.data;
  }

  static async removeMix(id: string, mixId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/channels/${id}/mixes/${mixId}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to remove mix');
  }
}
